import { useEffect, useRef } from 'react'

import { useAssistant, type AssistantApi } from '../../contexts/AssistantContext'
import type { ChatMessage } from '../../lib/assistant'
import { ABSENT_COPY, NOTICE_COPY, refusalCopy } from '../../lib/assistantCopy'
import { plainText } from '../../lib/assistantMarkdown'
import { FOLLOW_UPS, STARTERS, comparisonFollowUps, comparisonStarters, measureStarters } from '../../lib/assistantStarters'
import { Message } from './Message'
import { retryDeadline } from '../../lib/assistantThread'
import { useSecondsUntil } from './useRetryClock'

/**
 * The conversation (agent-1-web W3): starters when it is empty, the messages,
 * the follow-ups under a reply that opened a card, and the one notice that says
 * what happened to a turn.
 *
 * ── ANNOUNCING ──────────────────────────────────────────────────────────────
 *
 * A reply that arrives while the person waits is read out once, through a
 * polite live region holding only that reply's words (and its marker, for fixed
 * wording). The message list itself is NOT live: a reload or "show earlier"
 * would otherwise read a whole conversation aloud, and the trace is
 * `aria-live="off"` for the same reason.
 */
/**
 * `starters`: "always" in the drawer, where the canvas keeps them at the head
 * of the thread; "empty" on /assistant, whose canvas page shows a loaded
 * conversation without them.
 */
export function Thread({ className = '', starters = 'always' }: { className?: string; starters?: 'always' | 'empty' }) {
  const assistant = useAssistant()
  const scrollRef = useRef<HTMLDivElement>(null)
  const messages = assistant?.thread.messages

  // To the newest message when one arrives or grows -- not when earlier ones
  // are prepended, which would lose the reader's place (W2/W3 review).
  const newest = messages?.at(-1)
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [newest])
  const retryAt = assistant?.wait?.retryAt ?? retryDeadline(assistant?.thread.notice ?? null)
  const waiting = useSecondsUntil(retryAt) !== 0

  if (!assistant) return null
  const { thread } = assistant
  const live = thread.turn !== null
  // Whether anything may be sent now: the provider refuses too, this keeps
  // the chips honest about it.
  const blocked = live || thread.loading !== null || assistant.absent || waiting

  return (
    <div ref={scrollRef} className={`assistant-thread ${className}`.trim()}>
      {thread.loading && <p>Opening the conversation…</p>}
      {thread.nextBefore && (
        <button type="button" className="btn btn--ghost btn--sm assistant-thread__earlier" onClick={() => void assistant.loadEarlier()}>
          Show earlier messages
        </button>
      )}

      {/* At the head of the thread, empty or not, as the canvas keeps them. */}
      {!assistant.absent && (starters === 'always' || thread.messages.length === 0) && (
        <Starters assistant={assistant} disabled={blocked} />
      )}

      {thread.messages.map((message, i) => (
        <Message
          key={message.id}
          message={message}
          sessionId={thread.session?.id}
          live={live && message === thread.messages.at(-1)}
          // Only the newest reply offers "Try again": an older one would resend an old question.
          onRetry={message.retryable && message === thread.messages.at(-1) && !blocked ? () => retry(assistant, thread.messages, i) : undefined}
          onReload={thread.notice?.kind === 'error' ? undefined : assistant.reload}
        />
      ))}

      {!blocked && <FollowUps assistant={assistant} />}

      <ThreadNotice assistant={assistant} />

      {/* The ONE live region: mounted with the thread and empty until a reply
          arrives or a turn goes wrong (trap 28, liveRegions.test.ts). */}
      <div className="visually-hidden" role="status" aria-live="polite">
        {liveNotice(assistant) ?? announcement(thread.messages, thread.announce)}
      </div>
    </div>
  )
}

/** A retryable fallback's "Try again": the question that preceded it, as a new message. */
function retry(assistant: AssistantApi, messages: readonly ChatMessage[], at: number) {
  const question = messages
    .slice(0, at)
    .reverse()
    .find((m) => m.role === 'user')
  if (question?.text) assistant.send(question.text)
}

/**
 * What a notice says, for the live region. A refusal's countdown is left out:
 * a region that changes every second would read the clock aloud.
 */
function liveNotice(assistant: AssistantApi): string | null {
  if (assistant.thread.loading) return 'Opening the conversation…'
  if (assistant.absent) return ABSENT_COPY
  const notice = assistant.thread.notice
  switch (notice?.kind) {
    case undefined:
    case 'absent':
      return null
    case 'refused':
      return refusalCopy(notice.code, null)
    case 'dropped':
      return notice.code ? refusalCopy(notice.code, null) : notice.stillWriting ? NOTICE_COPY.stillWriting : NOTICE_COPY.dropped
    case 'error':
      return NOTICE_COPY.error
    case 'gone':
      return NOTICE_COPY.gone
    case 'invalid':
      return NOTICE_COPY.invalid
    case 'failed':
      return NOTICE_COPY.failed
  }
}

/** The words to announce: the reply that just arrived, without its Markdown marks. */
function announcement(messages: readonly ChatMessage[], id: string | null): string {
  const message = id === null ? undefined : messages.find((m) => m.id === id)
  return message?.text ? plainText(message.text) : ''
}

function Starters({ assistant, disabled }: { assistant: AssistantApi; disabled: boolean }) {
  const route = assistant.screen?.route ?? 'assistant'
  const experiment = route === 'compare' || route === 'experiment'
  const questions = assistant.measure ? measureStarters(assistant.measure) : experiment ? comparisonStarters(assistant.activityLabel) : STARTERS[route]
  return (
    <div className="assistant-starters">
      <p className="label assistant-starters__label">
        {assistant.measure ? `Ask about ${assistant.measure.title}` : experiment ? 'Ask about this experiment' : 'Ask about this screen'}
      </p>
      <div className="assistant-chips">
        {questions.map((q) => (
          <button key={q} type="button" className="assistant-chip" disabled={disabled} onClick={(event) => sendQuestion(assistant, q, event.currentTarget)}>
            {q}
          </button>
        ))}
      </div>
    </div>
  )
}

/** Under the newest reply, when it opened a card. */
function FollowUps({ assistant }: { assistant: AssistantApi }) {
  const last = assistant.thread.messages.at(-1)
  if (!last || last.role !== 'assistant' || last.source !== 'model') return null
  const experiment = assistant.screen?.route === 'compare' || assistant.screen?.route === 'experiment'
  const comparison = experiment && last.claims.some((claim) => claim.kind === 'comparison')
  if (!comparison && last.cards.length === 0) return null
  const questions = comparison ? comparisonFollowUps(assistant.activityLabel) : FOLLOW_UPS
  return (
    <div className="assistant-chips assistant-chips--follow">
      {questions.map((q) => (
        <button key={q} type="button" className="assistant-chip" onClick={(event) => sendQuestion(assistant, q, event.currentTarget)}>
          {q}
        </button>
      ))}
    </div>
  )
}

/** A comparison follow-up disappears during its turn; keep keyboard focus in the panel. */
function sendQuestion(assistant: AssistantApi, question: string, trigger: HTMLButtonElement) {
  assistant.send(question)
  if (assistant.screen?.route === 'compare' || assistant.screen?.route === 'experiment') {
    trigger.closest('.assistant-panel')?.querySelector<HTMLInputElement>('.assistant-composer__input')?.focus()
  }
}

function ThreadNotice({ assistant }: { assistant: AssistantApi }) {
  const notice = assistant.thread.notice
  const retryAt = assistant.wait?.retryAt ?? retryDeadline(notice ?? null)
  const seconds = useSecondsUntil(retryAt)

  if (assistant.absent) {
    return (
      <div className="assistant-notice assistant-notice--absent" >
        <p>{ABSENT_COPY}</p>
      </div>
    )
  }
  if (!notice) return seconds !== 0 && assistant.wait !== null ? (
    <div className="assistant-notice"><p>{refusalCopy(assistant.wait!.code, seconds)}</p></div>
  ) : null

  switch (notice.kind) {
    case 'refused':
      return (
        <div className="assistant-notice" >
          <p>{refusalCopy(notice.code, retryAt === null ? null : seconds)}</p>
        </div>
      )
    case 'dropped':
      return (
        <div className="assistant-notice" >
          <p>{notice.code ? refusalCopy(notice.code, retryAt === null ? null : seconds) : notice.stillWriting ? NOTICE_COPY.stillWriting : NOTICE_COPY.dropped}</p>
          <button type="button" className="btn btn--ghost btn--sm" disabled={seconds !== 0 || threadBlocked(assistant)} onClick={assistant.resendDropped}>
            Try again
          </button>
        </div>
      )
    case 'error':
      return (
        <div className="assistant-notice" >
          <p>{NOTICE_COPY.error}</p>
          <button type="button" className="btn btn--ghost btn--sm" onClick={assistant.reload}>
            Reload the conversation
          </button>
        </div>
      )
    case 'gone':
      return (
        <div className="assistant-notice" >
          <p>{NOTICE_COPY.gone}</p>
        </div>
      )
    case 'invalid':
      return (
        <div className="assistant-notice" >
          <p>{NOTICE_COPY.invalid}</p>
        </div>
      )
    case 'failed':
      return (
        <div className="assistant-notice" >
          <p>{NOTICE_COPY.failed}</p>
        </div>
      )
    case 'absent':
      return null
  }
}

function threadBlocked(assistant: AssistantApi): boolean {
  return assistant.thread.turn !== null || assistant.thread.loading !== null || assistant.absent
}
